/**
 * ShinyGoldButton — the platform's high-emphasis conversion pill
 * (2026-08-19 owner directive: "larger font shiny yellow pop out contrast
 * button"). One implementation shared by the pricing/subscription calculator
 * launcher, the pricing signup card, and the landing page CTAs, so the four
 * surfaces cannot drift apart.
 *
 * Design notes:
 *  - Brighter gold gradient than the standard house pill, with DARK ink text:
 *    white-on-gold is the legacy pill but fails contrast at this size, and the
 *    dark text is what makes the button read "yellow" against the page's muted
 *    golds rather than blending into them.
 *  - The ::after light band is the "shiny": one slow glint with a long dwell,
 *    disabled under prefers-reduced-motion.
 *  - GlobalStyles' 44px tap-target minimum on <button> is welcome here (real
 *    CTA) — nothing to defeat, unlike the calculator sliders.
 *  - text-transform: lowercase per house style; author labels in natural case.
 */
import styled, { keyframes } from 'styled-components';

const GOLD = 'var(--brand-gold)';
const GOLD_DEEP = 'var(--brand-gold-hover)';
const GOLD_INK = '#8A6F2F';

const shineSweep = keyframes`
  0%, 68% { transform: translateX(-140%) skewX(-18deg); }
  100% { transform: translateX(260%) skewX(-18deg); }
`;

const ShinyGoldButton = styled.button<{ $size?: 'md' | 'lg' }>`
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  border: none;
  cursor: pointer;
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: ${({ $size }) => ($size === 'lg' ? 'clamp(1.02rem, 2.4vw, 1.18rem)' : '1rem')};
  font-weight: 700;
  letter-spacing: -0.01em;
  text-transform: lowercase;
  color: #2B2208;
  background: linear-gradient(135deg, #EAD394 0%, ${GOLD} 48%, ${GOLD_DEEP} 100%);
  border-radius: 999px;
  padding: ${({ $size }) => ($size === 'lg' ? '1rem 2.1rem' : '0.85rem 1.9rem')};
  box-shadow: 0 8px 28px rgba(198, 167, 94, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.45);
  transition: transform 0.16s ease, box-shadow 0.16s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 34px rgba(198, 167, 94, 0.52), inset 0 1px 0 rgba(255, 255, 255, 0.5);
  }
  &:focus { outline: none; }
  &:focus-visible { outline: 3px solid ${GOLD_INK}; outline-offset: 3px; }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 42%;
    background: linear-gradient(105deg, transparent 0%, rgba(255, 255, 255, 0.5) 50%, transparent 100%);
    animation: ${shineSweep} 4.2s ease-in-out infinite;
    pointer-events: none;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &::after { animation: none; }
  }

  span[aria-hidden] { transition: transform 0.14s ease; }
  &:hover span[aria-hidden] { transform: translateX(3px); }
`;

export default ShinyGoldButton;
